import type {
  CSSProperties,
  FC,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from "react"
import { useCallback, useRef, useState } from "react"
import { area, curveMonotoneX, line } from "d3-shape"

import { ChevronsLeftRight, ZoomIn, ZoomOut } from "lucide-react"

import { cn } from "@/domains/library/cn"

import type { ThickV1TickDistributionBin } from "../../tick-distribution"
import {
  buildTickScale,
  computeBrushDomain,
  maxActiveLiquidity,
  pixelToSnappedTick,
  type RangeBrushDomain,
  tickDistanceInSpacings,
} from "./range-brush-helpers"

export type RangeBrushHandle = "min" | "max"

export interface RangeBrushViewProps {
  /** Liquidity distribution bins, already bucketed at `tickSpacing` resolution. */
  bins: ReadonlyArray<ThickV1TickDistributionBin>
  /** Pool's current tick from `slot0`. Undefined while pool data loads. */
  currentTick: number | undefined
  /** Pool tick spacing, used for snapping drag handles. */
  tickSpacing: number
  /** Currently-selected minimum tick (lower bound of user's range). */
  minTick: number
  /** Currently-selected maximum tick (upper bound of user's range). */
  maxTick: number
  /** Symbol for the base token of the displayed price. */
  baseSymbol: string
  /** Symbol for the quote token of the displayed price. */
  quoteSymbol: string
  /** Optional formatter used to render a tick as a human price string. */
  formatTickPrice?: (tick: number) => string
  /** Whether the base/quote are flipped relative to canonical token0/token1. */
  invert?: boolean
  /** Called when user toggles the base/quote direction. */
  onInvertToggle?: () => void
  /** Called when the user drags the lower handle to a new *snapped* tick. */
  onMinTickChange?: (tick: number) => void
  /** Called when the user drags the upper handle to a new *snapped* tick. */
  onMaxTickChange?: (tick: number) => void
  /**
   * Called when the user commits an edited value in the lower-bound pill.
   * The value is the raw string the user typed (in whatever unit the pill
   * currently shows — the composite applies the `invert` transform before
   * parsing to a tick). The view deliberately does not parse numbers itself;
   * it just hands the raw input back to the composite.
   */
  onMinPriceCommit?: (rawInput: string) => void
  /** Called when the user commits an edited value in the upper-bound pill. */
  onMaxPriceCommit?: (rawInput: string) => void
  /** Rendering height in pixels. Width is taken from the parent container. */
  height?: number
  /** Hide interactions and drag affordances (used on manage views). */
  readOnly?: boolean
  /** Show a loading placeholder; bins are still rendered muted if provided. */
  isLoading?: boolean
  /** Optional className appended to the root. */
  className?: string
  /** Width in pixels. Defaults to a sensible Storybook-friendly 640. */
  width?: number
  /**
   * Optional externally-owned visible domain. Create-position owns zoom state
   * in its composite, so passing the domain in prevents the view from
   * re-clamping against already-filtered bins and pinning handles to the
   * chart edges.
   */
  visibleDomain?: RangeBrushDomain
  /**
   * Fires when the user wants to tighten the visible tick window. The view
   * is pure: it just forwards the click; the composite is responsible for
   * actually re-computing `setWindow`. Optional — when omitted the button
   * renders in a disabled state.
   */
  onZoomIn?: () => void
  /** Fires when the user wants to widen the visible tick window. */
  onZoomOut?: () => void
  /** Controls the zoom-in button's enabled appearance. Defaults to true. */
  canZoomIn?: boolean
  /** Controls the zoom-out button's enabled appearance. Defaults to true. */
  canZoomOut?: boolean
  /**
   * Tick position of the historical low price for the current OHLC window
   * (e.g. the 7-day low). Rendered as a dashed vertical line so the user can
   * see where the price has actually travelled while dragging the range
   * handles. Hidden when outside the visible domain.
   */
  historicalLowTick?: number
  /** Tick position of the historical high price for the current OHLC window. */
  historicalHighTick?: number
  /**
   * Short label (e.g. "7d", "24h") applied to both historical reference
   * lines so the user knows which window they correspond to.
   */
  historicalWindowLabel?: string
}

const DEFAULT_WIDTH = 640
const DEFAULT_HEIGHT = 160
const SPLINE_PADDING_Y = 8
const HANDLE_HIT_WIDTH = 28
// "Bean" grip affordance on each draggable line. Sized so the bean is
// unmistakably a draggable pill at 100% zoom but never eclipses the
// liquidity profile behind it. Height is derived at render time so the
// bean scales with a custom `height` prop.
const BEAN_WIDTH = 10
const BEAN_HEIGHT_RATIO = 0.22
const BEAN_GRIP_WIDTH = 4

interface BuildSplinePathsParams {
  bins: ReadonlyArray<ThickV1TickDistributionBin>
  maxLiq: bigint
  scale: (tick: number) => number
  height: number
}

function buildSplinePaths({
  bins,
  maxLiq,
  scale,
  height,
}: BuildSplinePathsParams): { splinePath: string; outlinePath: string } {
  if (bins.length === 0 || maxLiq === 0n) {
    return { splinePath: "", outlinePath: "" }
  }
  const points = bins.map((bin) => {
    const midTick = (bin.tickLower + bin.tickUpper) / 2
    const ratio = Number((bin.activeLiquidity * 10_000n) / maxLiq) / 10_000
    const y = SPLINE_PADDING_Y + (1 - ratio) * (height - SPLINE_PADDING_Y * 2)
    return [scale(midTick), y] as [number, number]
  })
  const areaGen = area<[number, number]>()
    .x((d) => d[0])
    .y0(height - SPLINE_PADDING_Y)
    .y1((d) => d[1])
    .curve(curveMonotoneX)
  const lineGen = line<[number, number]>()
    .x((d) => d[0])
    .y((d) => d[1])
    .curve(curveMonotoneX)
  return {
    splinePath: areaGen(points) ?? "",
    outlinePath: lineGen(points) ?? "",
  }
}

/**
 * Pure View: the Uniswap-v3-style liquidity distribution with two draggable
 * price pills. Zero MobX coupling. All data arrives through plain DTO props;
 * drag state is a local `useState` because it is transient UI, not business
 * state. Drag math lives in `range-brush-helpers.ts` so it can be tested
 * without mounting React.
 *
 * Rendering strategy: the distribution is drawn as an SVG `area` (liquidity
 * profile) plus a muted `line` (same data, crisper outline) using `d3-shape`.
 * The x-axis uses `d3-scale.scaleLinear` over the tick domain computed by
 * `computeBrushDomain`. Two "pill" handles track `minTick`/`maxTick`; pointer
 * events handle drag without `d3-brush` so the component is jsdom-testable.
 */
export const RangeBrushView: FC<RangeBrushViewProps> = ({
  bins,
  currentTick,
  tickSpacing,
  minTick,
  maxTick,
  baseSymbol,
  quoteSymbol,
  formatTickPrice,
  invert = false,
  onInvertToggle,
  onMinTickChange,
  onMaxTickChange,
  onMinPriceCommit,
  onMaxPriceCommit,
  height = DEFAULT_HEIGHT,
  readOnly = false,
  isLoading = false,
  className,
  width = DEFAULT_WIDTH,
  visibleDomain,
  onZoomIn,
  onZoomOut,
  canZoomIn = true,
  canZoomOut = true,
  historicalLowTick,
  historicalHighTick,
  historicalWindowLabel,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [draggingHandle, setDraggingHandle] = useState<RangeBrushHandle | undefined>(undefined)
  // Transient UI state for inline pill editing. `editingHandle` tracks which
  // side is in "type mode"; `editingValue` is the working input buffer. Both
  // collapse back to `undefined` / "" on commit, blur, or Escape. We keep
  // this out of the store because it's purely transient UI ephemera — the
  // store only hears about it at commit time via `onMin/MaxPriceCommit`.
  const [editingHandle, setEditingHandle] = useState<RangeBrushHandle | undefined>(undefined)
  const [editingValue, setEditingValue] = useState<string>("")

  // No `useMemo` by project rule (see architect.mdc). These derivations are
  // cheap (O(bins) at worst, ~hundreds of items) and run at React's render
  // cadence — the work is dwarfed by the SVG paint that follows.
  const domain = visibleDomain ?? computeBrushDomain({ minTick, maxTick, currentTick, bins, tickSpacing })
  const scale = buildTickScale({ domain, width, invert })
  const maxLiq = maxActiveLiquidity(bins)
  const { splinePath, outlinePath } = buildSplinePaths({
    bins,
    maxLiq,
    scale,
    height,
  })

  const pointerEventToTick = useCallback(
    (event: ReactPointerEvent<Element>): number | undefined => {
      const svg = svgRef.current
      if (!svg) return undefined
      const rect = svg.getBoundingClientRect()
      if (rect.width === 0) return undefined
      const relativeX = ((event.clientX - rect.left) / rect.width) * width
      const clamped = Math.max(0, Math.min(width, relativeX))
      return pixelToSnappedTick({ x: clamped, scale, tickSpacing })
    },
    [scale, tickSpacing, width],
  )

  const onHandlePointerDown = useCallback(
    (handle: RangeBrushHandle) => (event: ReactPointerEvent<SVGGElement>) => {
      if (readOnly) return
      event.preventDefault()
      const target = event.currentTarget
      if (typeof target.setPointerCapture === "function") {
        try {
          target.setPointerCapture(event.pointerId)
        } catch {
          /* jsdom sometimes throws on capture without native support */
        }
      }
      setDraggingHandle(handle)
    },
    [readOnly],
  )

  const onHandlePointerMove = useCallback(
    (handle: RangeBrushHandle) => (event: ReactPointerEvent<SVGGElement>) => {
      if (readOnly) return
      if (draggingHandle !== handle) return
      const nextTick = pointerEventToTick(event)
      if (nextTick === undefined) return
      if (handle === "min") {
        if (nextTick >= maxTick) return
        onMinTickChange?.(nextTick)
      } else {
        if (nextTick <= minTick) return
        onMaxTickChange?.(nextTick)
      }
    },
    [draggingHandle, maxTick, minTick, onMaxTickChange, onMinTickChange, pointerEventToTick, readOnly],
  )

  const onHandlePointerUp = useCallback(
    (handle: RangeBrushHandle) => (event: ReactPointerEvent<SVGGElement>) => {
      if (draggingHandle !== handle) return
      const target = event.currentTarget
      if (
        typeof target.hasPointerCapture === "function" &&
        target.hasPointerCapture(event.pointerId)
      ) {
        try {
          target.releasePointerCapture(event.pointerId)
        } catch {
          /* noop */
        }
      }
      setDraggingHandle(undefined)
    },
    [draggingHandle],
  )

  const xMin = scale(minTick)
  const xMax = scale(maxTick)
  const xLeft = Math.min(xMin, xMax)
  const xRight = Math.max(xMin, xMax)
  const xCurrent = currentTick !== undefined ? scale(currentTick) : undefined
  const beanHeight = Math.max(18, Math.round(height * BEAN_HEIGHT_RATIO))
  const beanCenterY = height / 2

  // Historical reference lines: convert the tick values through the same
  // linear scale as the handles and hide any that fall outside the visible
  // viewport. We check inclusion against `domain` rather than pixel bounds
  // so that a zoomed-in user sees the line re-appear smoothly as soon as
  // the window opens up to include the tick again.
  const isTickInDomain = (tick: number): boolean =>
    tick >= domain.tickLower && tick <= domain.tickUpper
  const xHistoricalLow =
    historicalLowTick !== undefined && isTickInDomain(historicalLowTick)
      ? scale(historicalLowTick)
      : undefined
  const xHistoricalHigh =
    historicalHighTick !== undefined && isTickInDomain(historicalHighTick)
      ? scale(historicalHighTick)
      : undefined

  const minLabel = formatTickPrice?.(minTick) ?? `${minTick}`
  const maxLabel = formatTickPrice?.(maxTick) ?? `${maxTick}`
  const currentLabel =
    currentTick !== undefined ? (formatTickPrice?.(currentTick) ?? `${currentTick}`) : undefined

  const distanceLower =
    currentTick !== undefined
      ? tickDistanceInSpacings({ fromTick: currentTick, toTick: minTick, tickSpacing })
      : undefined
  const distanceUpper =
    currentTick !== undefined
      ? tickDistanceInSpacings({ fromTick: currentTick, toTick: maxTick, tickSpacing })
      : undefined

  const svgStyle: CSSProperties = { touchAction: "none" }

  return (
    <div
      className={cn(
        "bg-p400 border-p400-light text-t100 flex w-full flex-col gap-3 rounded-md border p-3",
        className,
      )}
      data-testid="range-brush-root"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-t200 text-xs uppercase tracking-wide">Liquidity</span>
          {isLoading ? (
            <span className="text-t300 text-xs">Loading…</span>
          ) : bins.length === 0 ? (
            <span className="text-t300 text-xs">No data</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <div
            className="bg-p300 border-p200-light inline-flex overflow-hidden rounded-full border"
            role="group"
            aria-label="Zoom liquidity distribution"
            data-testid="range-brush-zoom-controls"
          >
            <button
              type="button"
              onClick={onZoomOut}
              disabled={!onZoomOut || !canZoomOut}
              className={cn(
                "text-t200 hover:bg-p200-light disabled:hover:bg-p300 flex items-center justify-center px-2 py-1 transition-colors",
                (!onZoomOut || !canZoomOut) && "cursor-default opacity-40",
              )}
              aria-label="Zoom out liquidity distribution"
              data-testid="range-brush-zoom-out"
            >
              <ZoomOut className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onZoomIn}
              disabled={!onZoomIn || !canZoomIn}
              className={cn(
                "text-t200 hover:bg-p200-light disabled:hover:bg-p300 border-p200-light flex items-center justify-center border-l px-2 py-1 transition-colors",
                (!onZoomIn || !canZoomIn) && "cursor-default opacity-40",
              )}
              aria-label="Zoom in liquidity distribution"
              data-testid="range-brush-zoom-in"
            >
              <ZoomIn className="h-3 w-3" />
            </button>
          </div>
          <button
            type="button"
            onClick={onInvertToggle}
            disabled={!onInvertToggle}
            className={cn(
              "bg-p300 border-p200-light text-t200 hover:bg-p200-light flex items-center gap-1 rounded-full border px-2 py-1 text-xs transition-colors",
              !onInvertToggle && "cursor-default opacity-60",
            )}
            aria-label="Invert price direction"
            data-testid="range-brush-invert"
          >
            <ChevronsLeftRight className="h-3 w-3" />
            <span>
              {baseSymbol} per {quoteSymbol}
            </span>
          </button>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Tick liquidity distribution"
        className="h-40 w-full select-none"
        style={svgStyle}
        data-testid="range-brush-svg"
      >
        <defs>
          <linearGradient id="range-brush-gradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="hsl(var(--primary-400))" stopOpacity="0.35" />
            <stop offset="100%" stopColor="hsl(var(--primary-400))" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {splinePath ? (
          <path
            d={splinePath}
            fill="url(#range-brush-gradient)"
            data-testid="range-brush-spline"
          />
        ) : null}
        {outlinePath ? (
          <path
            d={outlinePath}
            fill="none"
            stroke="hsl(var(--primary-400))"
            strokeOpacity="0.8"
            strokeWidth="1.5"
            data-testid="range-brush-outline"
          />
        ) : null}

        {/* Selected range band */}
        <rect
          x={xLeft}
          y={SPLINE_PADDING_Y / 2}
          width={Math.max(0, xRight - xLeft)}
          height={height - SPLINE_PADDING_Y}
          fill="hsl(var(--primary-400))"
          fillOpacity="0.08"
          data-testid="range-brush-selection"
        />

        {/* Historical-window reference lines. Rendered underneath the
            current-tick line so the live price remains the most visually
            salient indicator; these are subtle context cues. */}
        {xHistoricalLow !== undefined ? (
          <g data-testid="range-brush-historical-low">
            <line
              x1={xHistoricalLow}
              x2={xHistoricalLow}
              y1={0}
              y2={height}
              stroke="hsl(var(--t300))"
              strokeOpacity="0.7"
              strokeWidth="1"
              strokeDasharray="5 3"
            />
            {historicalWindowLabel ? (
              <text
                x={xHistoricalLow + 3}
                y={11}
                fill="hsl(var(--t300))"
                fillOpacity="0.8"
                fontSize="9"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                data-testid="range-brush-historical-low-label"
              >
                {`L ${historicalWindowLabel}`}
              </text>
            ) : null}
          </g>
        ) : null}
        {xHistoricalHigh !== undefined ? (
          <g data-testid="range-brush-historical-high">
            <line
              x1={xHistoricalHigh}
              x2={xHistoricalHigh}
              y1={0}
              y2={height}
              stroke="hsl(var(--t300))"
              strokeOpacity="0.7"
              strokeWidth="1"
              strokeDasharray="5 3"
            />
            {historicalWindowLabel ? (
              <text
                x={xHistoricalHigh + 3}
                y={11}
                fill="hsl(var(--t300))"
                fillOpacity="0.8"
                fontSize="9"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                data-testid="range-brush-historical-high-label"
              >
                {`H ${historicalWindowLabel}`}
              </text>
            ) : null}
          </g>
        ) : null}

        {/* Current-price line */}
        {xCurrent !== undefined ? (
          <g data-testid="range-brush-current">
            <line
              x1={xCurrent}
              x2={xCurrent}
              y1={0}
              y2={height}
              stroke="hsl(var(--t200))"
              strokeOpacity="0.6"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
          </g>
        ) : null}

        {/* Lower handle */}
        <g
          data-testid="range-brush-handle-min"
          onPointerDown={onHandlePointerDown("min")}
          onPointerMove={onHandlePointerMove("min")}
          onPointerUp={onHandlePointerUp("min")}
          onPointerCancel={onHandlePointerUp("min")}
          style={{ cursor: readOnly ? "default" : "ew-resize" }}
        >
          <rect
            x={xMin - HANDLE_HIT_WIDTH / 2}
            y={0}
            width={HANDLE_HIT_WIDTH}
            height={height}
            fill="transparent"
          />
          <line
            x1={xMin}
            x2={xMin}
            y1={0}
            y2={height}
            stroke="hsl(var(--primary-400))"
            strokeWidth={draggingHandle === "min" ? 2 : 1.5}
            strokeOpacity={draggingHandle === "min" ? 1 : 0.8}
          />
          <HandleBean
            cx={xMin}
            cy={beanCenterY}
            height={beanHeight}
            isDragging={draggingHandle === "min"}
            readOnly={readOnly}
          />
        </g>

        {/* Upper handle */}
        <g
          data-testid="range-brush-handle-max"
          onPointerDown={onHandlePointerDown("max")}
          onPointerMove={onHandlePointerMove("max")}
          onPointerUp={onHandlePointerUp("max")}
          onPointerCancel={onHandlePointerUp("max")}
          style={{ cursor: readOnly ? "default" : "ew-resize" }}
        >
          <rect
            x={xMax - HANDLE_HIT_WIDTH / 2}
            y={0}
            width={HANDLE_HIT_WIDTH}
            height={height}
            fill="transparent"
          />
          <line
            x1={xMax}
            x2={xMax}
            y1={0}
            y2={height}
            stroke="hsl(var(--primary-400))"
            strokeWidth={draggingHandle === "max" ? 2 : 1.5}
            strokeOpacity={draggingHandle === "max" ? 1 : 0.8}
          />
          <HandleBean
            cx={xMax}
            cy={beanCenterY}
            height={beanHeight}
            isDragging={draggingHandle === "max"}
            readOnly={readOnly}
          />
        </g>
      </svg>

      <div className="flex items-center justify-between gap-2 text-xs">
        <EditablePricePill
          handle="min"
          label={minLabel}
          distance={distanceLower}
          isDragging={draggingHandle === "min"}
          isEditing={editingHandle === "min"}
          editingValue={editingValue}
          readOnly={readOnly || !onMinPriceCommit}
          onBeginEdit={(initialValue) => {
            setEditingHandle("min")
            setEditingValue(initialValue)
          }}
          onChange={setEditingValue}
          onCommit={(value) => {
            setEditingHandle(undefined)
            setEditingValue("")
            const trimmed = value.trim()
            if (trimmed.length === 0) return
            onMinPriceCommit?.(trimmed)
          }}
          onCancel={() => {
            setEditingHandle(undefined)
            setEditingValue("")
          }}
        />
        {currentLabel !== undefined ? (
          <div className="text-t200 flex flex-col items-center">
            <span className="text-t300 text-[10px] uppercase">Current</span>
            <span className="tabular-nums">{currentLabel}</span>
          </div>
        ) : null}
        <EditablePricePill
          handle="max"
          label={maxLabel}
          distance={distanceUpper}
          isDragging={draggingHandle === "max"}
          isEditing={editingHandle === "max"}
          editingValue={editingValue}
          readOnly={readOnly || !onMaxPriceCommit}
          onBeginEdit={(initialValue) => {
            setEditingHandle("max")
            setEditingValue(initialValue)
          }}
          onChange={setEditingValue}
          onCommit={(value) => {
            setEditingHandle(undefined)
            setEditingValue("")
            const trimmed = value.trim()
            if (trimmed.length === 0) return
            onMaxPriceCommit?.(trimmed)
          }}
          onCancel={() => {
            setEditingHandle(undefined)
            setEditingValue("")
          }}
        />
      </div>
    </div>
  )
}

interface HandleBeanProps {
  cx: number
  cy: number
  height: number
  isDragging: boolean
  readOnly: boolean
}

/**
 * The grip affordance that sits on the middle of each draggable handle line.
 * Drawn as a pointer-events-none decoration so it never intercepts the
 * surrounding `<rect>` hit area — the parent `<g>` still owns drag capture.
 * On hover / drag the bean brightens and the grip lines go fully opaque,
 * giving a subtle "ready to drag" cue without animating anything expensive.
 */
const HandleBean: FC<HandleBeanProps> = ({ cx, cy, height, isDragging, readOnly }) => {
  const beanX = cx - BEAN_WIDTH / 2
  const beanY = cy - height / 2
  const rx = BEAN_WIDTH / 2
  const gripX1 = cx - BEAN_GRIP_WIDTH / 2
  const gripX2 = cx + BEAN_GRIP_WIDTH / 2
  const gripOffset = height * 0.18
  return (
    <g pointerEvents="none" data-testid="range-brush-handle-bean">
      <rect
        x={beanX}
        y={beanY}
        width={BEAN_WIDTH}
        height={height}
        rx={rx}
        ry={rx}
        fill="hsl(var(--primary-400))"
        fillOpacity={readOnly ? 0.5 : isDragging ? 1 : 0.9}
        stroke="hsl(var(--p500))"
        strokeWidth="1"
      />
      <line
        x1={gripX1}
        x2={gripX2}
        y1={cy - gripOffset}
        y2={cy - gripOffset}
        stroke="hsl(var(--p500))"
        strokeOpacity={readOnly ? 0.5 : isDragging ? 1 : 0.85}
        strokeWidth="1"
        strokeLinecap="round"
      />
      <line
        x1={gripX1}
        x2={gripX2}
        y1={cy}
        y2={cy}
        stroke="hsl(var(--p500))"
        strokeOpacity={readOnly ? 0.5 : isDragging ? 1 : 0.85}
        strokeWidth="1"
        strokeLinecap="round"
      />
      <line
        x1={gripX1}
        x2={gripX2}
        y1={cy + gripOffset}
        y2={cy + gripOffset}
        stroke="hsl(var(--p500))"
        strokeOpacity={readOnly ? 0.5 : isDragging ? 1 : 0.85}
        strokeWidth="1"
        strokeLinecap="round"
      />
    </g>
  )
}

interface EditablePricePillProps {
  handle: RangeBrushHandle
  label: string
  distance: number | undefined
  isDragging: boolean
  isEditing: boolean
  editingValue: string
  readOnly: boolean
  onBeginEdit: (initialValue: string) => void
  onChange: (next: string) => void
  onCommit: (value: string) => void
  onCancel: () => void
}

/**
 * Local sub-component: the Min/Max pill that swaps its price span for a text
 * input on click. Kept inside this module so `RangeBrushView` stays the sole
 * export and storybook/tests keep a single render surface.
 *
 * The input is uncontrolled from the composite's perspective — the view owns
 * the working buffer. On Enter (or blur), the raw string is handed to the
 * composite via `onCommit`; the composite is responsible for parsing and
 * applying the `invert` transform. Escape discards the edit.
 */
const EditablePricePill: FC<EditablePricePillProps> = ({
  handle,
  label,
  distance,
  isDragging,
  isEditing,
  editingValue,
  readOnly,
  onBeginEdit,
  onChange,
  onCommit,
  onCancel,
}) => {
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      onCommit(event.currentTarget.value)
    } else if (event.key === "Escape") {
      event.preventDefault()
      onCancel()
    }
  }

  const isMin = handle === "min"
  const labelEl = (
    <span className="text-t300 text-[10px] uppercase">{isMin ? "Min" : "Max"}</span>
  )
  const distanceEl =
    distance !== undefined ? (
      <span className="text-t300 text-[10px]">{Math.abs(distance)} ticks</span>
    ) : null

  const priceSlot = isEditing ? (
    <input
      type="text"
      inputMode="decimal"
      value={editingValue}
      autoFocus
      onChange={(event) => onChange(event.target.value)}
      onBlur={(event) => onCommit(event.target.value)}
      onKeyDown={handleKeyDown}
      className="bg-p200 text-t100 w-28 rounded border-none px-1 font-medium outline-none tabular-nums focus:ring-1 focus:ring-primary-400"
      data-testid={`range-brush-pill-${handle}-input`}
      aria-label={`${isMin ? "Minimum" : "Maximum"} price`}
    />
  ) : (
    <button
      type="button"
      onClick={readOnly ? undefined : () => onBeginEdit(label)}
      disabled={readOnly}
      className={cn(
        "font-medium tabular-nums",
        !readOnly && "hover:text-t100 cursor-text",
        readOnly && "cursor-default",
      )}
      data-testid={`range-brush-pill-${handle}-label`}
    >
      {label}
    </button>
  )

  return (
    <div
      className={cn(
        "bg-p300 border-p200-light text-t100 flex items-center gap-2 rounded-full border px-3 py-1",
        isDragging && "ring-primary-400/60 ring-1",
        isEditing && "ring-primary-400 ring-1",
      )}
      data-testid={`range-brush-pill-${handle}`}
    >
      {isMin ? (
        <>
          {labelEl}
          {priceSlot}
          {distanceEl}
        </>
      ) : (
        <>
          {distanceEl}
          {priceSlot}
          {labelEl}
        </>
      )}
    </div>
  )
}
